/**
 * ClockInDialog — PIN entry + action buttons for kiosk
 */

import { useState, useCallback } from 'react';
import { PinPad } from './PinPad';
import type { KioskStaffState } from '../../stores/workforce-store';

interface ClockInDialogProps {
  staffState: KioskStaffState;
  onClockIn: (staffId: string, pin: string) => Promise<boolean>;
  onClockOut: (staffId: string, pin: string) => Promise<boolean>;
  onBreakStart: (staffId: string, pin: string) => Promise<boolean>;
  onBreakEnd: (staffId: string, pin: string) => Promise<boolean>;
  onClose: () => void;
  isProcessing: boolean;
}

type Step = 'pin' | 'action' | 'success';

export function ClockInDialog({
  staffState,
  onClockIn,
  onClockOut,
  onBreakStart,
  onBreakEnd,
  onClose,
  isProcessing,
}: ClockInDialogProps) {
  const [step, setStep] = useState<Step>('pin');
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState('');

  const { staff, status } = staffState;

  const handlePinSubmit = useCallback((enteredPin: string) => {
    setPin(enteredPin);
    setError(null);
    setStep('action');
  }, []);

  const handleAction = useCallback(async (action: 'in' | 'out' | 'break-start' | 'break-end') => {
    let success = false;
    let message = '';

    switch (action) {
      case 'in':
        success = await onClockIn(staff.id, pin);
        message = 'Ingeklokt!';
        break;
      case 'out':
        success = await onClockOut(staff.id, pin);
        message = 'Uitgeklokt!';
        break;
      case 'break-start':
        success = await onBreakStart(staff.id, pin);
        message = 'Pauze gestart';
        break;
      case 'break-end':
        success = await onBreakEnd(staff.id, pin);
        message = 'Pauze beeindigd';
        break;
    }

    if (success) {
      setSuccessMessage(message);
      setStep('success');
      setTimeout(onClose, 2000);
    } else {
      setError('Ongeldige PIN of actie niet mogelijk');
      setStep('pin');
      setPin('');
    }
  }, [staff.id, pin, onClockIn, onClockOut, onBreakStart, onBreakEnd, onClose]);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div
        className="bg-white rounded-3xl p-8 max-w-md w-full mx-4 shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <span className="text-2xl font-bold text-indigo-600">
              {staff.name.charAt(0).toUpperCase()}
            </span>
          </div>
          <h2 className="text-xl font-semibold text-slate-800">{staff.name}</h2>
          <p className="text-sm text-slate-500">{staff.team || 'Algemeen'}</p>
        </div>

        {/* Step: PIN */}
        {step === 'pin' && (
          <div>
            <p className="text-center text-slate-600 mb-4">Voer je PIN in</p>
            <PinPad
              onSubmit={handlePinSubmit}
              onCancel={onClose}
              isProcessing={isProcessing}
              error={error}
            />
          </div>
        )}

        {/* Step: Action */}
        {step === 'action' && (
          <div className="space-y-3">
            {status === 'out' && (
              <button
                onClick={() => handleAction('in')}
                disabled={isProcessing}
                className="w-full py-4 rounded-2xl bg-emerald-500 text-white text-lg font-semibold hover:bg-emerald-600 active:bg-emerald-700 transition-colors disabled:opacity-50"
              >
                Inklokken
              </button>
            )}

            {status === 'in' && (
              <>
                <button
                  onClick={() => handleAction('break-start')}
                  disabled={isProcessing}
                  className="w-full py-4 rounded-2xl bg-amber-500 text-white text-lg font-semibold hover:bg-amber-600 active:bg-amber-700 transition-colors disabled:opacity-50"
                >
                  Pauze
                </button>
                <button
                  onClick={() => handleAction('out')}
                  disabled={isProcessing}
                  className="w-full py-4 rounded-2xl bg-red-500 text-white text-lg font-semibold hover:bg-red-600 active:bg-red-700 transition-colors disabled:opacity-50"
                >
                  Uitklokken
                </button>
              </>
            )}

            {status === 'break' && (
              <>
                <button
                  onClick={() => handleAction('break-end')}
                  disabled={isProcessing}
                  className="w-full py-4 rounded-2xl bg-emerald-500 text-white text-lg font-semibold hover:bg-emerald-600 active:bg-emerald-700 transition-colors disabled:opacity-50"
                >
                  Pauze beeindigen
                </button>
                <button
                  onClick={() => handleAction('out')}
                  disabled={isProcessing}
                  className="w-full py-4 rounded-2xl bg-red-500 text-white text-lg font-semibold hover:bg-red-600 active:bg-red-700 transition-colors disabled:opacity-50"
                >
                  Uitklokken
                </button>
              </>
            )}

            <button
              onClick={() => { setStep('pin'); setPin(''); }}
              className="w-full py-3 rounded-2xl text-slate-500 hover:bg-slate-50 transition-colors"
            >
              Terug
            </button>
          </div>
        )}

        {/* Step: Success */}
        {step === 'success' && (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-xl font-semibold text-slate-800">{successMessage}</p>
            <p className="text-sm text-slate-500 mt-1">
              {new Date().toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
